import { useState } from 'react';
import { Link } from 'react-router-dom';
import Alert from './Alert';
import { useMe } from '../hooks/useMe';

/**
 * RecoveryNudge (#2930 S3a) — a calm, dismissible, non-blocking prompt for a
 * phone-only user who has replied STOP and will be locked out of their account
 * once their live session lapses (their only address is the synthetic
 * `{phone}@sms.oro.fit`, which has no inbox, so magic-link sign-in can't reach
 * them). It asks them to add a real email while they still hold the key.
 *
 * Reads the `recovery_status` hint off `/auth/me` and renders ONLY for
 * `tier1_locked` — the population that is actually locked out. `tier2`
 * (has a real email, only inconvenienced) is deliberately NOT nudged here; it
 * is deferred to S3b, which also owns its persisted "dismiss forever".
 *
 * Dismissal is per-session only (local state, not persisted): visibility is
 * derived from live server state, which self-clears the instant they add an
 * email (the synthetic address flips to real, the hint drops to `none` and the
 * field disappears). So a dismissal lasts the session and the nudge re-appears
 * on the next app open while they remain at risk — "matters enough to repeat,"
 * without ever blocking the app.
 */
export default function RecoveryNudge() {
  const { data: me } = useMe();
  const [dismissed, setDismissed] = useState(false);

  if (dismissed || me?.recovery_status !== 'tier1_locked') return null;

  return (
    <Alert
      variant="info"
      title="Add an email to keep access"
      dismissible
      onDismiss={() => setDismissed(true)}
      className="mb-4"
    >
      You&apos;ve opted out of our texts, which includes your sign-in codes.{' '}
      <Link to="/settings/profile" className="font-semibold underline">
        Add an email address
      </Link>{' '}
      so you can always get back in.
    </Alert>
  );
}
